import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AccessDenied } from "@/components/AccessDenied";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { CreditCard, RefreshCw } from "lucide-react";
import { getStripeEnvironment } from "@/lib/stripe";
import { listAllSubscriptions } from "@/lib/admin-subscriptions.functions";
import { StripeSyncCard } from "@/components/admin/StripeSyncCard";

export const Route = createFileRoute("/admin/predplatna")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Přehled předplatných — Admin" },
      { name: "description", content: "Všechna předplatná a jejich stav, synchronizace se Stripe." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString("cs-CZ") : "—");

function StatusBadge({ effective, status }: { effective: string; status: string | null }) {
  if (effective === "active") return <Badge>Aktivní ({status})</Badge>;
  if (effective === "pending")
    return <Badge variant="secondary">Čeká na aktivaci ({status ?? "bez stavu"})</Badge>;
  return <Badge variant="outline">Ukončeno ({status})</Badge>;
}

function Page() {
  const { hasRole, loading } = useAuth();
  const env = getStripeEnvironment();
  const fetchList = useServerFn(listAllSubscriptions);

  const query = useQuery({
    queryKey: ["admin", "predplatna", env],
    queryFn: () => fetchList({ data: { environment: env } }),
    enabled: hasRole("admin"),
    retry: 2,
  });

  const rows = useMemo(() => query.data?.subscriptions ?? [], [query.data]);
  const pending = useMemo(() => query.data?.pendingOrders ?? [], [query.data]);
  const activeCount = rows.filter((r) => r.effective === "active").length;
  const pendingCount = rows.filter((r) => r.effective === "pending").length + pending.length;

  if (loading) return null;
  if (!hasRole("admin")) return <AccessDenied />;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-serif text-2xl font-semibold">
          <CreditCard className="h-6 w-6" /> Přehled předplatných
        </h1>
        <Button
          size="sm"
          variant="outline"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} />
          Obnovit
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary">Celkem: {rows.length}</Badge>
        <Badge>Aktivních: {activeCount}</Badge>
        <Badge variant="secondary">Čeká na aktivaci: {pendingCount}</Badge>
      </div>

      <StripeSyncCard />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Předplatná v databázi</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Načítám…</p>
          ) : query.isError ? (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                Seznam se nepodařilo načíst
                {query.error instanceof Error ? `: ${query.error.message}` : ""}
              </p>
              <Button size="sm" variant="outline" onClick={() => query.refetch()}>
                Zkusit znovu
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Zatím žádná předplatná — spusťte synchronizaci se Stripe výše.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Uživatel</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Produkt / cena</TableHead>
                  <TableHead>Konec období</TableHead>
                  <TableHead>Reklamní odkaz</TableHead>
                  <TableHead>Stripe ID</TableHead>
                  <TableHead>Aktualizováno</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {r.user_name ?? r.user_email ?? r.user_id.slice(0, 8)}
                      </div>
                      {r.user_email && r.user_name && (
                        <div className="text-xs text-muted-foreground">{r.user_email}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge effective={r.effective} status={r.status} />
                      {r.cancel_at_period_end && (
                        <div className="mt-1 text-xs text-muted-foreground">zrušeno k konci období</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{r.price_id ?? "—"}</TableCell>
                    <TableCell className="text-sm">{fmt(r.current_period_end)}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-sm">
                      {r.link_url ? (
                        <span>
                          {r.link_url}{" "}
                          <Badge variant="outline" className="ml-1">
                            {r.link_status ?? "?"}
                          </Badge>
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.stripe_subscription_id ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm">{fmt(r.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Čekající objednávky bez předplatného</CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">Žádné čekající objednávky.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Uživatel</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Částka</TableHead>
                  <TableHead>Vytvořeno</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {o.user_name ?? o.user_email ?? o.user_id.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{o.kind}</TableCell>
                    <TableCell className="text-sm">
                      {o.amount != null ? `${(o.amount / 100).toFixed(2)} ${o.currency ?? ""}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{fmt(o.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
